import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Typography, Button, Box } from '@mui/material';

import TextField from '../text-field/TextField';
import InputTags from '../inputTags/InputTags';

import {
  useGetAuthenticationQuery,
  useGetDomainControllersQuery,
  useCreateDomainControllerMutation,
} from '../../services/ADapi';
import { notificationSuccess } from '../../store/notificationReducer';

import { validateIpAddress } from '../../utils/validatateIP';

import styles from '../inputTags/InputTags.module.scss';

const DomainControllerForm = () => {
  const dispatch = useDispatch();
  const {
    data: { user_id },
  } = useGetAuthenticationQuery();

  const { data: controllers } = useGetDomainControllersQuery(user_id);
  const [createDomainController] = useCreateDomainControllerMutation();

  const [controllerName, setControllerName] = useState('');
  const [isControllerFormValid, setDomainControllerFormValid] = useState(false);
  const [controllerErrors, setDomainControllerErrors] = useState({});

  const [tags, setTags] = useState([]);

  useEffect(() => {
    const domainNameNotEmpty = controllerName !== '';
    const areTagsAdded = tags.length > 0;
    const allTagsValid = tags.every((tag) => tag.className === '');

    setDomainControllerFormValid(
      domainNameNotEmpty && allTagsValid && areTagsAdded
    );
  }, [controllerName, tags]);

  const domainControllerNameHandler = (e) => {
    setControllerName(e.target.value);
    const errorsCopy = { ...controllerErrors };
    delete errorsCopy.name;
    setDomainControllerErrors(errorsCopy);
  };

  const handleDelete = (index) => {
    setDomainControllerErrors((prevState) => {
      return {
        ...prevState,
        // When there are messages from regexp check, there is no such object in error state
        ips: prevState.ips?.filter((_, i) => i !== index),
      };
    });

    setTags(tags.filter((_, i) => i !== index));
  };

  const onTagUpdate = (index, newTag) => {
    const isValid = validateIpAddress(newTag.text);

    if (!isValid) {
      newTag.className = styles['ReactTags__tag_error'];
    }

    const updatedTags = [...tags];
    updatedTags.splice(index, 1, newTag);

    setTags(updatedTags);
  };

  const handleAddition = (tag) => {
    const isValid = validateIpAddress(tag.text);

    if (!isValid) {
      tag.className = styles['ReactTags__tag_error'];
    }

    // If there are some spaces before and after a semicolon, then there will be incorrect behavior
    tag.text = tag.text.replaceAll(/\s*;\s*|;/g, '');
    tag.id = tag.id.replaceAll(/\s*;\s*|;/g, '');

    setTags((prevTags) => {
      return [...prevTags, tag];
    });
  };

  const createDomainControllerHandler = async () => {
    const ips = tags.map((tag) => ({
      ip: tag.text,
    }));

    try {
      await createDomainController({
        user_id,
        domainController: {
          name: controllerName,
          // Only first controller can be primary, so if there's no controllers yet, first one should be primary
          is_pdc: !controllers.length,
          ips,
        },
      }).unwrap();
      setTags([]);
      setControllerName('');
      setDomainControllerErrors({});
      setDomainControllerFormValid(true);
      dispatch(
        notificationSuccess({
          isNotification: true,
          type: 'success',
          message: 'Domain controller created successfully',
        })
      );
    } catch (errors) {
      setDomainControllerErrors(errors);
      setDomainControllerFormValid(false);
    }
  };

  return (
    <>
      <Typography variant="h1" component="h2">
        Domain Controllers
      </Typography>
      <Box sx={{ minWidth: '400px' }}>
        <TextField
          id="domainControllerName"
          name="domain-contoller-name"
          label="Create Domain Controller"
          value={controllerName}
          placeholder="Name"
          size="small"
          onChange={domainControllerNameHandler}
        />
        {controllerErrors.name ? (
          <Typography variant="body2" color="#e08d8d">
            {controllerErrors.name}
          </Typography>
        ) : null}
      </Box>
      <Box>
        <InputTags
          tags={tags}
          handleAddition={handleAddition}
          handleDelete={handleDelete}
          onTagUpdate={onTagUpdate}
        />
        {controllerErrors.ips
          ? controllerErrors.ips.map((item, i) => (
              <Typography
                key={`${i}-${item.ip}`}
                variant="body2"
                color="#e08d8d"
              >
                {item.ip}
              </Typography>
            ))
          : null}
      </Box>

      <Button
        type="submit"
        variant="contained"
        disabled={!isControllerFormValid}
        size="large"
        sx={{
          minWidth: '243px',
          '&.Mui-disabled': {
            backgroundColor: 'primary.light',
            color: '#fff',
          },
        }}
        onClick={createDomainControllerHandler}
      >
        Create Domain Controller
      </Button>
      {controllerErrors.non_field_errors ? (
        <Typography variant="body2" color="#e08d8d">
          {controllerErrors.non_field_errors}
        </Typography>
      ) : null}
    </>
  );
};

export default DomainControllerForm;
